import type {
  ExperienceLevel,
  Interest,
  UseCase,
  UserRole,
} from '@/components/onboarding/types';
import type { Language } from '@/i18n/translations';

/**
 * Human-readable persona descriptors used in AI system prompts.
 *
 * We never pass raw enum values (e.g. 'power-user') to the LLM; we transform
 * them to self-explanatory text (e.g. 'a senior technical user who works
 * quickly'). Two reasons:
 *   1. Debug logs stay readable even when a new enum value is added.
 *   2. If the enum string changes, the prompt text stays stable.
 */

type Localized = Partial<Record<Language, string>> & { en: string };

export const EXPERIENCE_DESCRIPTORS: Record<ExperienceLevel, Localized> = {
  beginner: {
    sv: 'nybörjare som aldrig har använt visuella workflow-verktyg tidigare',
    en: 'a beginner who has never used visual workflow tools before',
  },
  experienced: {
    sv: 'erfaren användare av visuella verktyg (Miro, Lucidchart, Figma)',
    en: 'an experienced user of visual tools (Miro, Lucidchart, Figma)',
  },
  'power-user': {
    sv: 'senior power-user som bygger komplexa workflows ofta',
    en: 'a senior power user who builds complex workflows frequently',
  },
};

export const ROLE_DESCRIPTORS: Record<UserRole, Localized> = {
  designer: {
    sv: 'designer med fokus på UX, visualisering och processdesign',
    en: 'designer focused on UX, visualization and process design',
  },
  developer: {
    sv: 'utvecklare som bygger automation och API-integrationer',
    en: 'developer building automation and API integrations',
  },
  business: {
    sv: 'business-användare inom projektledning, processer eller analys',
    en: 'business user in project management, processes or analysis',
  },
  other: {
    sv: 'användare med generell bakgrund',
    en: 'a user with a general background',
  },
};

export const USECASE_DESCRIPTORS: Record<UseCase, Localized> = {
  'it-infrastructure': {
    sv: 'IT-infrastruktur och nätverks-topologi',
    en: 'IT infrastructure and network topology',
  },
  'business-process': {
    sv: 'affärsprocesser och workflows',
    en: 'business processes and workflows',
  },
  'organization-chart': {
    sv: 'organisationsscheman och team-strukturer',
    en: 'organization charts and team structures',
  },
  'decision-tree': {
    sv: 'beslutsträd, troubleshooting och regel-logik',
    en: 'decision trees, troubleshooting and rule logic',
  },
  'mind-map': {
    sv: 'mindmaps och idé-utforskning',
    en: 'mind maps and idea exploration',
  },
  'project-planning': {
    sv: 'projektplanering med milstolpar och beroenden',
    en: 'project planning with milestones and dependencies',
  },
  sandbox: {
    sv: 'fri utforskning utan specifik inriktning',
    en: 'free exploration without a specific focus',
  },
};

export const INTEREST_DESCRIPTORS: Record<Interest, Localized> = {
  'ai-assistant': { sv: 'AI-driven workflow-generering', en: 'AI-driven workflow generation' },
  '3d-view':      { sv: '3D-visualisering av multi-sheet workflows', en: '3D visualization of multi-sheet workflows' },
  'multi-sheet':  { sv: 'flera sheets och cross-sheet connectors', en: 'multiple sheets and cross-sheet connectors' },
  'export-share': { sv: 'PNG/SVG-export och cloud workspaces', en: 'PNG/SVG export and cloud workspaces' },
  shortcuts:      { sv: 'tangentbord-shortcuts och snabbare workflows', en: 'keyboard shortcuts and faster workflows' },
  'process-motor': { sv: 'process-motor: kör workflows som processer', en: 'process engine: run workflows as processes' },
  enterprise:     { sv: 'Enterprise-funktioner: API-nycklar, webhooks, scheman', en: 'Enterprise features: API keys, webhooks, schedules' },
};
